import React, { useState, useEffect, useMemo } from "react";
import { useSnackBar } from "../../../hooks/useSnackBar";
import "./orders.css";
import "./Components/OrderDetails/OrderDetails.css";
import OrdersTable from "../../../Components/BoltTable/BoltTable";
import BulkActionBar, {
  BulkActionButton,
} from "../../../Components/BoltTable/Components/BulkActionBar";
import FullfilmentTotalDetailsModal from "../../../Pages/Shipper/Customer/MainPages/Orders/Components/FullfilmentTotalDetails/FullfilmentTotalDetails";
import DetailsModal from "./Components/OrderDetails/DetailsModal";
import InvoiceModal from "../../../Components/InvoiceModal/InvoiceModal";
import {
  fetchAllWarehouseOrders,
  fetchOrderCount,
  getUserWarehousesByWar_Id,
} from "../../../services/warehouse.services";
import { fetchOrderStatuses } from "../../../services/orders.services";
import CircularProgress from "@mui/material/CircularProgress";
import OrderHistoryModal from "../../Shipper/Customer/MainPages/Orders/Components/OrderHistoryModal/OrderHistoryModal";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AttachmentsModal from "./Components/AttachmentsModal/AttachmentsModal";
import StatusIcons from "../../../Components/StatusIcons/StatusIcons";
import ActionDropDown from "./Components/ActionDropDown/ActionDropDown";
import { PDFDocument } from "pdf-lib";
import Box from "@mui/material/Box";
import OrderCell from "../../Shipper/Customer/MainPages/Orders/Components/Cells/OrderCell";
import CustomerCell from "../../Shipper/Customer/MainPages/Orders/Components/Cells/CustomerCell";
import ItemsCell from "../../Shipper/Customer/MainPages/Orders/Components/Cells/ItemsCell";
import ShippingCell from "../../Shipper/Customer/MainPages/Orders/Components/Cells/ShippingCell";
import ShippingDocs from "./Components/ShippingDocs/ShippingDocs";
import FulfilmentCell from "../../Shipper/Customer/MainPages/Orders/Components/Cells/FulfilmentCell";
import ActionsCell from "../../Shipper/Customer/MainPages/Orders/Components/Cells/ActionsCell";
import {
  orderHeaderStats,
  withCounts,
} from "../../Shipper/Customer/MainPages/Orders/orderHeaderStats";
import {
  carrierFilterOptions,
  fulfilmentBillingFilterOptions,
  fulfilmentFilterOptions,
  shipmentStatusFilterOptions,
  typeFilterOptions,
} from "../../../constants/filterOptions";
import { useTranslation } from "react-i18next";
import { useFormat } from "../../../i18n/format";

// Known countries for the Customer heading (same as Shipper Orders); any
// other can be typed.
const countryOptions = [
  { code: "CA", name: "Canada", value: "Canada" },
  { code: "US", name: "United States", value: "United States" },
];

// A heading number from a counted pop-up option, in its status chip's
// text colour; clicking it applies that option, the same &field=value as
// picking it in the heading pop-up. `labelFor(count)` gives "304 self pickup".
const optionStat = (options, field_name, value, labelFor, tone, title) => ({
  key: value,
  label: labelFor(options.find((option) => option.value === value)?.count ?? "0"),
  tone,
  title,
  filter: { field_name, value },
});

const WarehouseOrder = () => {
  const { t } = useTranslation(["warehouse", "common"]);
  const { formatCountry } = useFormat();
  const [orders, setOrders] = useState([]);
  const [prodHistoryModal, setProdHistoryModal] = useState(false);
  const [fulBillingDetailsModal, setFulBillingDetailsModal] = useState(false);
  const [imagesModal, setImagesModal] = useState();
  const [clients, setClients] = useState([]);
  const [orderStatus, setorderStatus] = useState([]);

  // All-time counts for the numbers under the headings and in the heading
  // pop-ups (they replaced the metrics cards).
  const [headingCounts, setHeadingCounts] = useState(null);
  const headerStats = useMemo(
    () => orderHeaderStats(headingCounts),
    [headingCounts],
  );
  // The heading pop-up options with their counts (all dates).
  const typeOptions = useMemo(
    () => withCounts(typeFilterOptions, headingCounts, "type"),
    [headingCounts],
  );
  const fulfilmentOptions = useMemo(
    () => withCounts(fulfilmentFilterOptions, headingCounts, "fulfilment"),
    [headingCounts],
  );
  // Order heading: all orders and self pickups; Fulfilment heading: new and
  // packing. The other counts are in the heading pop-ups.
  const orderStats = useMemo(
    () =>
      headingCounts && [
        ...(headerStats.order ?? []),
        optionStat(
          typeOptions,
          "type",
          "self pickup",
          (count) => t("orders.statSelfPickup", { count: Number(count) }),
          "success",
          t("orders.statSelfPickupTitle"),
        ),
      ],
    [headingCounts, headerStats, typeOptions, t],
  );
  const fulfilmentStats = useMemo(
    () =>
      headingCounts && [
        optionStat(
          fulfilmentOptions,
          "fulfillment_status",
          "pending",
          (count) => t("orders.statNew", { count: Number(count) }),
          "neutral",
          t("orders.statNewTitle"),
        ),
        optionStat(
          fulfilmentOptions,
          "fulfillment_status",
          "packing",
          (count) => t("orders.statPacking", { count: Number(count) }),
          "info",
          t("orders.statPackingTitle"),
        ),
      ],
    [headingCounts, fulfilmentOptions, t],
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [showLabelOptions, setShowLabelOptions] = useState(false);
  const [isBulkLabelMerging, setIsBulkLabelMerging] = useState(false);
  const [bulkLabelProgress, setBulkLabelProgress] = useState("");

  const setSnack = useSnackBar();

  // Used by the commented-out column below.
  // eslint-disable-next-line no-unused-vars
  const defaultWidth = "200px";

  function isCancelled(status) {
    const lowerCaseStatus = status ? status.toLowerCase() : null;
    return lowerCaseStatus === "cancelled";
  }

  // Dropdown filter options for the toolbar filters.

  const orderStatusFilterOptions = [
    { label: t("orders.confirmed"), value: "confirmed" },
    { label: t("orders.onHold"), value: "on hold" },
    { label: t("orders.cancelled"), value: "cancelled" },
    // { label: "Fulfilled", value: "fulfilled" },
  ];

  const columns = [
    {
      id: 0,
      label: t("orders.order"),
      headerStats: orderStats,
      field_name: "order_id",
      // Type and Created At live in this cell now (created time on hover).
      // The heading pop-up holds the order number search and Type.
      headerFilters: [
        { label: t("orders.orderNumber"), field_name: "order_id" },
        {
          label: t("orders.type"),
          field_name: "type",
          options: typeOptions,
        },
      ],
      render: (row) => (
        <OrderCell
          row={row}
          // The order number opens the order view (the redesigned one).
          onOpen={(order) =>
            setdetailModal([order?.id, false, undefined, true])
          }
          showMarketplace={false}
        />
      ),
      width: "200px",
      headerColor: "var(--color-brand-light)",
      fixed: "left",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 1,
      label: t("orders.client"),
      field_name: "username",
      headerFilters: [
        { label: t("orders.client"), field_name: "username", options: clients },
      ],
      render: (row) => {
        const client = row?.user?.username;
        return (
          <span title={client || undefined} className="block min-w-0 truncate">
            {client ?? "-"}
          </span>
        );
      },
      width: "140px",
      disabled: (row) => isCancelled(row?.status),
    },
    // {
    //   id: 2,
    //   label: "Order ID",
    //   field_name: "order_id",
    //   searchable: true,
    //   render: (row) => <>{row?.order_id ?? "-"}</>,
    //   width: defaultWidth,
    //   disabled: (row) => isCancelled(row?.status),
    // },
    {
      id: 4,
      label: t("orders.customer"),
      field_name: "first_name",
      headerFilters: [
        { label: t("orders.name"), field_name: "first_name" },
        // Matches the country code or name by partial text (backend).
        {
          label: t("common:fields.country"),
          field_name: "country",
          // The names in the reader's language; the value sent stays the English name.
          options: countryOptions.map(({ code, name, value }) => ({
            label: formatCountry(code, name),
            value,
          })),
        },
      ],
      render: (row) => <CustomerCell row={row} />,
      width: "146px",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 5,
      label: t("orders.items"),
      render: (row) => <ItemsCell row={row} />,
      width: "196px",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 6,
      label: t("orders.status"),
      field_name: "status",
      // Any other status can be typed (the backend matches partial text).
      headerFilters: [
        {
          label: t("orders.orderStatus"),
          field_name: "status",
          options: orderStatusFilterOptions,
        },
      ],
      render: (row) => (
        <div className="flex min-w-0 max-w-full">
          <StatusIcons status={row?.status} />
        </div>
      ),
      width: "150px",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 7,
      label: t("orders.shipping"),
      headerStats: headerStats.shipping,
      // Not a filter field, so chips take the heading sections' labels.
      field_name: "shipping",
      // Carrier, Packages, Tracking, Reference and Shipment Status live in
      // this cell now, with the Label / Decl downloads; all their filters
      // are in the heading pop-up.
      headerFilters: [
        { label: t("orders.trackingNumber"), field_name: "shipment_tracking_id" },
        { label: t("orders.reference"), field_name: "referance_code" },
        {
          label: t("orders.carrier"),
          field_name: "company_name",
          options: carrierFilterOptions,
        },
        {
          label: t("orders.shipmentStatus"),
          field_name: "shipment_status",
          options: shipmentStatusFilterOptions,
        },
      ],
      render: (row) => (
        <ShippingCell
          row={row}
          showAmount={false}
          actions={
            <ShippingDocs
              row={row}
              printLabel={printLabel}
              printDeclaration={printDeclaration}
            />
          }
        />
      ),
      width: "280px",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 11,
      label: t("orders.fulfilment"),
      headerStats: fulfilmentStats,
      // Not a filter field, so chips take the heading sections' labels.
      field_name: "fulfilment",
      // Fulfilment, Total and Billing live in this cell now; their filters
      // are in the heading pop-up.
      headerFilters: [
        {
          label: t("orders.fulfilmentStatus"),
          field_name: "fulfillment_status",
          options: fulfilmentOptions,
          // Several statuses at once, or every one still to fulfil.
          multiple: true,
          presets: [
            {
              label: t("orders.notFulfilledYet"),
              values: ["pending", "packing", "attention"],
              hint: t("orders.pendingNewPackingAndAttention"),
            },
          ],
        },
        {
          // The server matches billing exactly, so typed text can't help.
          label: t("orders.billing"),
          field_name: "fulfillment_billing_status",
          options: withCounts(
            fulfilmentBillingFilterOptions,
            headingCounts,
            "billing",
          ),
          freeText: false,
        },
      ],
      // Same rules as before: any warehouse user may change the status (no
      // permission check here); the fee and billing show on every order.
      render: (row) => (
        <FulfilmentCell
          row={row}
          orderStatus={orderStatus}
          canEdit={true}
          isDefaultLocation={false}
          showLocation={false}
          onStatusChange={handlestatusChange}
          onOpenCost={setFulBillingDetailsModal}
        />
      ),
      width: "240px",
      disabled: (row) => isCancelled(row?.status),
    },
    {
      id: 18,
      label: t("orders.actions"),
      field_name: "Actions",
      // Attachments live here now: paperclip and count (or "+"), opening
      // the same attachments pop-up; then the row menu.
      render: (row) => (
        <ActionsCell
          row={row}
          onOpenAttachments={setImagesModal}
          menu={
            <ActionDropDown
              setdetailModal={setdetailModal}
              setProdHistoryModal={setProdHistoryModal}
              setInvoiceModal={setInvoiceModal}
              handleOrderShipemnt={handleOrderShipemnt}
              printLabel={printLabel}
              printDeclaration={printDeclaration}
              labelDownloading={labelDownloading}
              CircularProgress={CircularProgress}
              row={row}
              compact={true}
            />
          }
        />
      ),
      width: "84px",
      fixed: "right",
      disabled: (row) => isCancelled(row?.status),
    },
  ];

  const getAllOrders = async (page, perPage, filter, nestedSearch) => {
    let response;
    response = await fetchAllWarehouseOrders(
      page,
      perPage,
      filter,
      user.warehouse_id,
      nestedSearch,
    );
    if (!response.error) {
      setOrders(response?.data?.result);
    }
  };

  const printDeclaration = async (row) => {
    setLabelDownloading("Declaration" + row?.id);
    try {
      fetch(row?.shipment?.single_shipping_invoice.file, {
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      })
        .then((url) => url.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${
              row?.shipment?.tattension +
              " " +
              row?.shipment?.shipment_tracking_id
            } Declaration.pdf`,
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          setLabelDownloading("");
        });
    } catch (error) {
      setLabelDownloading("");
    }
  };

  const [labelDownloading, setLabelDownloading] = useState("");
  const [invoiceModal, setInvoiceModal] = useState();
  const [detailModal, setdetailModal] = useState(false);

  const user = useSelector((state) => state.user);
  const Navigate = useNavigate();
  const printLabel = async (row) => {
    setLabelDownloading("label" + row?.id);
    try {
      fetch(row?.shipment?.single_shipping_labels.file, {
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      })
        .then((url) => url.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${
              row?.shipment?.tattension +
              " " +
              row?.shipment?.shipment_tracking_id
            } Label.pdf`,
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          setLabelDownloading("");
        });
    } catch (error) {
      setLabelDownloading("");
    }
  };
  const calculateTotalCost = (data) => {
    let totalCost = 0;
    let total = 0;
    if (data?.additional_costs?.length > 0) {
      data.additional_costs.forEach((currentItem) => {
        const cost = parseFloat(currentItem.cost ?? 0);
        totalCost += cost;
      });
    }
    total = totalCost + parseFloat(data.fulfillment_cost ?? 0);
    return parseFloat(total).toFixed(2);
  };
  const calculateTotalAdditionalCost = (data) => {
    let totalCost = 0;
    if (data?.additional_costs?.length > 0) {
      data.additional_costs.forEach((currentItem) => {
        const cost = parseFloat(currentItem.cost);
        totalCost += cost;
      });
    }
    return totalCost;
  };
  const handleOrderShipemnt = (row) => {
    localStorage.setItem("OrderShipment", JSON.stringify(row));
    Navigate("/");
  };

  const getAllWarehouseClients = async () => {
    const response = await getUserWarehousesByWar_Id(
      user?.warehouse_id,
      1,
      50,
      "",
    );
    if (!response.error) {
      const clients = (response?.data?.result?.data || [])
        .map((item) => ({
          label: item?.shipper?.username
            ? item.shipper.username.charAt(0).toUpperCase() +
              item.shipper.username.slice(1)
            : "",
          value: item?.shipper?.username,
        }))
        .filter((o) => o.label);
      setClients(clients);
    }
  };

  const getOrderStatuses = async () => {
    let response;
    response = await fetchOrderStatuses();
    if (!response.error) {
      setorderStatus(response?.data?.result);
    }
  };

  useEffect(() => {
    getOrderStatuses();
    getAllWarehouseClients();
  }, []);

  // The heading numbers cover all dates, like the list (no dates sent).
  useEffect(() => {
    const getHeadingCounts = async () => {
      const { data, error } = await fetchOrderCount("", "");
      if (data) {
        setHeadingCounts(data.result);
      } else {
        console.error("Error fetching order count:", error);
      }
    };
    getHeadingCounts();
  }, []);

  const handlestatusChange = (e, row) => {
    setdetailModal([row?.id, false, e.target.value]);
  };

  const handleBulkLabelMerged = async () => {
    setShowLabelOptions(false);
    setIsBulkLabelMerging(true);
    setBulkLabelProgress(t("orders.progressPreparing"));
    try {
      const selectedOrders = orders?.data?.filter((o) =>
        selectedRows.includes(o.id),
      );
      const mergedPdf = await PDFDocument.create();
      let hasLabels = false;
      setBulkLabelProgress(t("orders.progressFetching"));
      for (const row of selectedOrders) {
        const labelUrls = [];
        if (row?.shipment?.single_shipping_labels?.file) {
          labelUrls.push(row.shipment.single_shipping_labels.file);
        } else if (row?.manual_shipment?.shipping_labels?.length > 0) {
          row.manual_shipment.shipping_labels.forEach((l) =>
            labelUrls.push(l.file),
          );
        }
        for (const url of labelUrls) {
          try {
            const pdfBytes = await fetch(url).then((res) => res.arrayBuffer());
            const pdf = await PDFDocument.load(pdfBytes);
            const copiedPages = await mergedPdf.copyPages(
              pdf,
              pdf.getPageIndices(),
            );
            copiedPages.forEach((page) => mergedPdf.addPage(page));
            hasLabels = true;
          } catch (err) {
            console.error("Error loading label PDF:", url, err);
          }
        }
      }
      if (hasLabels) {
        setBulkLabelProgress(t("orders.progressSaving"));
        const mergedPdfBytes = await mergedPdf.save();
        const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Merged_Labels_${new Date().getTime()}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        setSnack({
          open: true,
          text: t("orders.noLabelsFoundForSelectedOrders"),
          severity: "warning",
        });
      }
    } catch (error) {
      console.error("Error merging labels:", error);
      setSnack({ open: true, text: t("orders.errorMergingLabels"), severity: "error" });
    } finally {
      setIsBulkLabelMerging(false);
      setBulkLabelProgress("");
    }
  };

  const handleBulkLabel = async () => {
    setShowLabelOptions(false);
    setIsBulkLabelMerging(true);
    setBulkLabelProgress(t("orders.progressDownloading"));
    try {
      const selectedOrders = orders?.data?.filter((o) =>
        selectedRows.includes(o.id),
      );
      let count = 0;
      const total = selectedOrders.length;
      for (const row of selectedOrders) {
        count++;
        setBulkLabelProgress(`${count}/${total}`);
        if (row?.shipment?.single_shipping_labels?.file) {
          await printLabel(row);
        } else if (row?.manual_shipment?.shipping_labels?.length > 0) {
          for (const [
            index,
            label,
          ] of row.manual_shipment.shipping_labels.entries()) {
            const response = await fetch(label.file);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `${row.manual_shipment.shipment_tracking_id}_Label_${index + 1}.pdf`,
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            await new Promise((r) => setTimeout(r, 300));
          }
        }
      }
    } catch (error) {
      console.error("Error downloading individual labels:", error);
      setSnack({
        open: true,
        text: t("orders.errorDownloadingLabels"),
        severity: "error",
      });
    } finally {
      setIsBulkLabelMerging(false);
      setBulkLabelProgress("");
    }
  };

  const handleBulkPackingList = () => {
    if (selectedRows.length > 0) {
      setdetailModal([selectedRows, true]);
    }
  };

  return (
    <div className="dispatcher__screen !max-w-full !px-[5px] !pt-[5px] !pb-0">
      <FullfilmentTotalDetailsModal
        open={fulBillingDetailsModal}
        setOpen={setFulBillingDetailsModal}
        calculateTotalCost={calculateTotalCost}
        calculateTotalAdditionalCost={calculateTotalAdditionalCost}
      />
      <AttachmentsModal
        open={imagesModal}
        setOpen={setImagesModal}
        setSnack={setSnack}
        setOrders={setOrders}
      />
      <OrderHistoryModal
        open={prodHistoryModal}
        setOpen={setProdHistoryModal}
        setSnack={setSnack}
        setOrders={setOrders}
      />
      <DetailsModal
        open={detailModal}
        setOpen={setdetailModal}
        orders={orders}
        setOrders={setOrders}
        orderStatus_initial={orderStatus}
        setSnack={setSnack}
        editable={true}
      />
      <InvoiceModal
        open={!!invoiceModal}
        data={invoiceModal}
        setOpen={setInvoiceModal}
        actions={false}
      />

      <div className="custom__table_size1 actionbtn__needtobe_fixwidth">
        <OrdersTable
          tableTitle={t("orders.orders")}
          // New key for the merged layout: column ids now mean merged
          // columns, so settings saved for the old columns must not hide them.
          tableId="ordersTableforWarehouse_v2"
          message={t("orders.noOrdersAvailableToDisplay")}
          searchPlaceHolder={t("orders.searchOrders")}
          columns={columns}
          data={orders}
          getAllData={getAllOrders}
          selectable={true}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />

        <BulkActionBar
          count={selectedRows.length}
          onClear={() => setSelectedRows([])}
        >
          <div className="relative">
            <BulkActionButton
              label={
                bulkLabelProgress
                  ? t("orders.labelsProgress", { progress: bulkLabelProgress })
                  : t("orders.labelsCaps")
              }
              onClick={() => setShowLabelOptions(!showLabelOptions)}
              disabled={isBulkLabelMerging}
              icon={
                isBulkLabelMerging
                  ? () => (
                      <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    )
                  : null
              }
            />
            {showLabelOptions && (
              <div className="absolute bottom-full left-0 mb-2 w-48 bg-ui-surface border border-ui-border rounded-lg shadow-xl z-[999] overflow-hidden animate-in fade-in zoom-in duration-200">
                <button
                  onClick={handleBulkLabelMerged}
                  className="w-full text-left px-4 py-2.5 text-[10px] font-jakarta font-bold text-ui-text hover:bg-ui-surface-sunken hover:text-brand transition-colors border-b border-ui-surface-sunken"
                >
                  {t("orders.downloadMergedPdfCaps")}
                </button>
                <button
                  onClick={handleBulkLabel}
                  className="w-full text-left px-4 py-2.5 text-[10px] font-jakarta font-bold text-ui-text hover:bg-ui-surface-sunken hover:text-brand transition-colors"
                >
                  {t("orders.downloadSeparatePdfsCaps")}
                </button>
              </div>
            )}
          </div>

          <BulkActionButton
            label={t("orders.packingListsCaps")}
            onClick={handleBulkPackingList}
          />
        </BulkActionBar>
      </div>
    </div>
  );
};

export default WarehouseOrder;
